import { ACCOUNT_ROLES } from '@/api/models';
import type {
    AccountRole,
    AccountRoleTag,
    AccountType,
    NormalBalance,
} from '@/api/models';
import type { ParseGelResult } from '../../utils';

/**
 * Presentational config + pure text mappers for the account create/edit form —
 * kept out of the `.tsx` files per the repo's render/logic split. Option lists
 * carry i18n key references (namespace `pages`); the View resolves them with `t`.
 */

/** A parent-picker option (group / non-postable account). */
export type ParentAccountOption = {
    id: string;
    code: string;
    name: string;
    /** `"code name"` so the ComboBox contains-filter matches either. */
    searchText: string;
};

/** VAT role choices. The `NONE` sentinel maps to a `null` `vatRole` on write. */
export type VatRoleChoice = 'INPUT' | 'OUTPUT' | 'NONE';

export const ACCOUNT_TYPE_OPTIONS: {
    value: AccountType;
    labelKey: string;
}[] = [
    { value: 'ASSET', labelKey: 'chartOfAccounts.types.asset' },
    { value: 'LIABILITY', labelKey: 'chartOfAccounts.types.liability' },
    { value: 'EQUITY', labelKey: 'chartOfAccounts.types.equity' },
    { value: 'REVENUE', labelKey: 'chartOfAccounts.types.revenue' },
    { value: 'EXPENSE', labelKey: 'chartOfAccounts.types.expense' },
];

export const NORMAL_BALANCE_OPTIONS: {
    value: NormalBalance;
    labelKey: string;
}[] = [
    {
        value: 'DEBIT',
        labelKey: 'chartOfAccounts.form.normalBalanceOptions.debit',
    },
    {
        value: 'CREDIT',
        labelKey: 'chartOfAccounts.form.normalBalanceOptions.credit',
    },
];

export const VAT_ROLE_OPTIONS: { value: VatRoleChoice; labelKey: string }[] = [
    { value: 'NONE', labelKey: 'chartOfAccounts.form.vatRoleOptions.none' },
    { value: 'INPUT', labelKey: 'chartOfAccounts.form.vatRoleOptions.input' },
    { value: 'OUTPUT', labelKey: 'chartOfAccounts.form.vatRoleOptions.output' },
];

/** Normalise a stored `vatRole` into the select's choice key. */
export const toVatRoleChoice = (vatRole: string | null): VatRoleChoice =>
    vatRole === 'INPUT' || vatRole === 'OUTPUT' ? vatRole : 'NONE';

/** The write value for a chosen VAT role (`NONE` → `null`). */
export const fromVatRoleChoice = (choice: VatRoleChoice): string | null =>
    choice === 'NONE' ? null : choice;

/**
 * Ledger-flow role choices, in the order they occur in a period: open the books,
 * carry last year's result, park this year's, then the two disposal outcomes.
 * `NONE` maps to a `null` `accountRole` on write, as `vatRole` does.
 */
export type AccountRoleChoice = AccountRole;

/**
 * Keyed by the role union rather than hand-written as an array: adding a role to
 * `ACCOUNT_ROLES` then fails to compile here until it has a label, instead of
 * silently rendering a select the new role is missing from.
 */
const ACCOUNT_ROLE_LABEL_KEYS: Record<AccountRoleChoice, string> = {
    NONE: 'chartOfAccounts.form.accountRoleOptions.none',
    OPENING_BALANCE_EQUITY:
        'chartOfAccounts.form.accountRoleOptions.openingBalanceEquity',
    RETAINED_EARNINGS:
        'chartOfAccounts.form.accountRoleOptions.retainedEarnings',
    CURRENT_YEAR_RESULT:
        'chartOfAccounts.form.accountRoleOptions.currentYearResult',
    DISPOSAL_GAIN: 'chartOfAccounts.form.accountRoleOptions.disposalGain',
    DISPOSAL_LOSS: 'chartOfAccounts.form.accountRoleOptions.disposalLoss',
};

/** Options in the source list's order — see `ACCOUNT_ROLES`. */
export const ACCOUNT_ROLE_OPTIONS: {
    value: AccountRoleChoice;
    labelKey: string;
}[] = ACCOUNT_ROLES.map((value) => ({
    value,
    labelKey: ACCOUNT_ROLE_LABEL_KEYS[value],
}));

/** Normalise a stored `accountRole` into the select's choice key. */
export const toAccountRoleChoice = (
    accountRole: AccountRoleTag,
): AccountRoleChoice => accountRole ?? 'NONE';

/** The write value for a chosen role (`NONE` → `null`). */
export const fromAccountRoleChoice = (
    choice: AccountRoleChoice,
): AccountRoleTag => (choice === 'NONE' ? null : choice);

/** i18n key for a code-field error (client collision or server rejection). */
export const CODE_ERROR_KEY: Record<'required' | 'duplicate', string> = {
    required: 'chartOfAccounts.form.errors.codeRequired',
    duplicate: 'chartOfAccounts.form.errors.codeDuplicate',
};

/** i18n key for an opening-balance parse error (empty is not an error). */
export const openingBalanceErrorKey = (
    result: ParseGelResult,
): string | null => {
    if (result.ok) {
        return null;
    }
    switch (result.reason) {
        case 'empty':
            return null;
        case 'sub-tetri':
            return 'chartOfAccounts.form.errors.openingBalanceSubTetri';
        case 'negative':
            return 'chartOfAccounts.form.errors.openingBalanceNegative';
        default:
            return 'chartOfAccounts.form.errors.openingBalanceInvalid';
    }
};

/** A client-side account id for idempotent, retry-safe create. */
export const newAccountId = (): string =>
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `acc-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
